import { useCallback, useRef, useState } from "react";
import {
  Box,
  Command,
  CommandInput,
  DrawerPinned,
  CommandItem,
  CommandList,
  EmptyState,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  TabsViewIcon,
  Text,
  ViewTabsRailButton,
} from "@salestrak/ui";

import type { EntityViewRow, EntityViewType } from "#app/lib/data";
import { useViews } from "#app/lib/views/view-context";
import { VIEW_TYPE_META } from "./view-type-meta";

/**
 * The tab row's `+ View`: adds a saved view to the scope.
 *
 * A view added here is WORKSPACE-SHARED — it becomes a tab for everyone — so
 * `create.views` gates it, and a viewer without the grant gets no trigger at
 * all: the menu renders nothing, and the host drops the rail hairline that
 * would otherwise introduce it. The server seeds the built-ins, so this only
 * ever adds a view beside them.
 *
 * On a phone, once the strip folds views into its "{n} more…" roster, this
 * trigger folds too: the roster's inline "New View" group (see
 * `ViewOverflowMenu`) hosts the same act through `useAddView`, and the strip
 * ends seamlessly at the roster trigger instead of a hairline and a second
 * control.
 *
 * Grammar: the searchable-catalog popover the create menu established — a
 * fixed-width Popover hosting the inline Command, its search a bordered well
 * floating inset rather than a flush ⌘K header row. The field takes focus on
 * open, so the type is one word away on the keyboard; the tiles stay a
 * two-track grid, which is what the type list has always been. The query
 * is a label match on the types, and the placeholder says only that. A line
 * under the tiles says the new view is shared with the workspace, since the
 * create itself never asks.
 */

/** The view types a scope can add — the picker's order. */
export const ADDABLE_VIEW_TYPES: readonly EntityViewType[] = [
  "list",
  "board",
  "dashboard",
];

/**
 * The name a new view takes: its type's label, numbered past any view in the
 * scope that already holds it ("List", then "List 2", "List 3"), so two tabs
 * never read the same. Letter case is ignored, as a reader would.
 */
export function uniqueViewName(base: string, existing: readonly string[]): string {
  const taken = new Set(existing.map((name) => name.trim().toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

export type AddViewMenuProps = {
  /** The route owns navigation — the menu reports what it made. */
  onCreated?: (view: EntityViewRow) => void;
};

/**
 * The add-view act behind every trigger that offers it: the `create.views`
 * gate, the in-flight flag, and the create call itself. The tab row's
 * `+ View` popover and the folded roster's "New View" group share it, so a
 * view made from either lands with the same seed identity.
 */
export function useAddView(onCreated?: (view: EntityViewRow) => void) {
  const { canManage, createView, views } = useViews();
  const [creating, setCreating] = useState(false);

  const create = useCallback(
    async (type: EntityViewType) => {
      setCreating(true);
      try {
        // Stamp the type's seed identity so the new tab wears the same glyph and
        // color the picker showed; the server would otherwise store its
        // colorless default.
        const meta = VIEW_TYPE_META[type];
        const created = await createView(
          {
            type,
            name: uniqueViewName(
              meta.label,
              views.map((view) => view.name),
            ),
            icon: { icon: meta.icon, color: meta.color },
          },
          "add_menu",
        );
        if (created) onCreated?.(created);
      } finally {
        setCreating(false);
      }
    },
    [createView, onCreated, views],
  );

  return { canManage, creating, create };
}

export function AddViewMenu({ onCreated }: AddViewMenuProps) {
  const { canManage, creating, create } = useAddView(onCreated);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Reset the filter per open, not on close — clearing on close would make
  // the tiles visibly reshuffle under the popover's exit transition.
  const onOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) setQuery("");
    setOpen(isOpen);
  }, []);

  const handleCreate = useCallback(
    (type: EntityViewType) => {
      setOpen(false);
      void create(type);
    },
    [create],
  );

  if (!canManage) return null;

  const q = query.trim().toLowerCase();
  const types = ADDABLE_VIEW_TYPES.filter((type) =>
    VIEW_TYPE_META[type].label.toLowerCase().includes(q),
  );

  return (
    // `responsive`: a bottom sheet on a phone — the search takes the keyboard.
    <Popover onOpenChange={onOpenChange} open={open} responsive>
      <PopoverTrigger
        disabled={creating}
        render={<ViewTabsRailButton aria-label="Add view" />}
      >
        <Icon name="plus" size="md" />
        View
      </PopoverTrigger>
      <PopoverPopup
        aria-label="Add view"
        align="start"
        width="panel"
        // The search is what this popover opens onto — the tiles are a mouse
        // field, the field is the keyboard route in.
        initialFocus={searchRef}
        padding="none"
        sheetTitle="Add view"
        side="bottom"
      >
        <Command mode="none" onValueChange={setQuery} value={query}>
          {/* In the phone sheet the search pins above the scroller. */}
          <DrawerPinned>
            <CommandInput
              inset
              placeholder="Search view types..."
              ref={searchRef}
              startAddon={null}
            />
          </DrawerPinned>
          <CommandList>
            {types.length === 0 ? (
              <Box align="center" justify="center" paddingY="md">
                <EmptyState size="xs" title="No views found" />
              </Box>
            ) : (
              <Box columns={2} gap="2xs">
                {types.map((type) => {
                  const meta = VIEW_TYPE_META[type];
                  return (
                    <CommandItem
                      key={type}
                      onClick={() => handleCreate(type)}
                      value={type}
                    >
                      <Box align="center" direction="row" gap="sm">
                        <TabsViewIcon
                          color={meta.color}
                          icon={meta.tabIcon}
                          variant="choice-tile"
                        />
                        {meta.label}
                      </Box>
                    </CommandItem>
                  );
                })}
              </Box>
            )}
          </CommandList>
          <Box paddingX="sm" paddingY="xs">
            <Text size="xs" variant="muted">
              A new view is shared with everyone in the workspace.
            </Text>
          </Box>
        </Command>
      </PopoverPopup>
    </Popover>
  );
}
